import { supabase } from '@/integrations/supabase/client';
import type { ArrivalsParams, ArrivalsResponse, ArrivalsAPIResponse, ItemCodesResponse } from '@/types/arrivals';

const BASE_URL = 'https://api-styling-teijin.factory-pilot.ai';

// Cache for item codes - loaded once per app startup
let itemCodesCache: string[] | null = null;

async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No authentication token available');
  }
  return session.access_token;
}

/**
 * Fetches arrivals from the API and normalizes the response
 * API returns snake_case (items, page_size), we normalize to camelCase
 */
export async function fetchArrivals(params: ArrivalsParams = {}): Promise<ArrivalsResponse> {
  const token = await getAuthToken();

  const queryParams = new URLSearchParams();
  if (params.itemCode) queryParams.append('itemCode', params.itemCode);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

  const url = `${BASE_URL}/arrivals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch arrivals: ${response.statusText}`);
  }

  const apiResponse: ArrivalsAPIResponse = await response.json();

  // Normalize API response to internal format
  return {
    data: apiResponse.items,
    pagination: {
      page: apiResponse.pagination.page,
      pageSize: apiResponse.pagination.page_size,
      total: apiResponse.pagination.total_items,
      totalPages: apiResponse.pagination.total_pages,
    },
  };
}

export async function fetchItemCodes(forceRefresh = false): Promise<string[]> {
  // Return cached data if available and not forcing refresh
  if (itemCodesCache && !forceRefresh) {
    return itemCodesCache;
  }
  
  const token = await getAuthToken();
  
  const response = await fetch(`${BASE_URL}/item-codes`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch item codes: ${response.statusText}`);
  }
  
  const data: ItemCodesResponse = await response.json();
  itemCodesCache = data.item_codes;
  return data.item_codes;
}

// Export function to clear cache if needed
export function clearItemCodesCache() {
  itemCodesCache = null;
}
